import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { titleTheme, pageTheme, headerTheme, contentTheme, subTheme, cardTheme } from '../../components/consoleTheme'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { RefreshCw, Boxes, Clock3, Cpu } from 'lucide-react'

const Page = styled.main`
  ${pageTheme}
min-height:100vh;background:#f8fafc;color:#1e293b;min-width:0;`
const Header = styled.header`
  ${headerTheme}
display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-wrap:wrap;padding:1rem 2rem;background:white;border-bottom:1px solid #e2e8f0;@media(max-width:768px){padding:1rem;align-items:stretch;button{min-height:44px;}}`
const Title = styled.h1`
 ${titleTheme}
font-size:1.25rem;font-weight:600;margin:0;`
const Sub = styled.p`
  ${subTheme}
font-size:.82rem;color:#64748b;margin:.3rem 0 0;`
const Refresh = styled.button`display:inline-flex;align-items:center;gap:.4rem;padding:.5rem .8rem;border:1px solid #cbd5e1;border-radius:6px;background:white;cursor:pointer;&:disabled{opacity:.5;cursor:default;}`
const Content = styled.div`
  ${contentTheme}
padding:1.5rem 2rem;max-width:1500px;min-width:0;@media(max-width:768px){padding:1rem;}`
const Note = styled.p`padding:.8rem 1rem;background:#eff6ff;border:1px solid #bfdbfe;border-radius:7px;color:#1e40af;font-size:.85rem;line-height:1.5;`
const ErrorNotice = styled.p`padding:1rem;background:#fef2f2;border:1px solid #fecaca;border-radius:7px;color:#991b1b;`
const Card = styled.section`
  ${cardTheme}
background:white;border:1px solid #e2e8f0;border-radius:8px;margin:1.2rem 0;overflow:hidden;`
const CardTitle = styled.h2`display:flex;align-items:center;gap:.45rem;font-size:1rem;margin:0;padding:1rem 1.2rem;border-bottom:1px solid #e2e8f0;`
const Empty = styled.p`color:#64748b;padding:1.1rem 1.2rem;margin:0;`
const TableScroll = styled.div`overflow-x:auto;max-width:100%;-webkit-overflow-scrolling:touch;`
const Table = styled.table`width:100%;border-collapse:collapse;font-size:.85rem;th,td{padding:.8rem 1rem;text-align:left;border-bottom:1px solid #f1f5f9;vertical-align:top;}th{color:#475569;background:#f8fafc;font-weight:600;white-space:nowrap;}td{overflow-wrap:anywhere;}tr:last-child td{border-bottom:0;}@media(max-width:768px){width:max-content;min-width:100%;th,td{padding:.7rem .8rem;}}`
const Mono = styled.code`font-family:ui-monospace,Menlo,Consolas,monospace;`
const HostBlock = styled.div`padding:.9rem 1.2rem;border-bottom:1px solid #e2e8f0;&:last-child{border-bottom:0;}`
const HostName = styled.h3`display:flex;align-items:center;gap:.45rem;flex-wrap:wrap;font-size:.95rem;margin:0;`
const Tag = styled.span`font-size:.7rem;font-weight:600;padding:.1rem .4rem;border-radius:4px;white-space:nowrap;
  background:${p => (p.$warn ? '#fff7ed' : '#eff6ff')};color:${p => (p.$warn ? '#9a3412' : '#1e40af')};
  border:1px solid ${p => (p.$warn ? '#fed7aa' : '#bfdbfe')};`

// Hosts are rendered from the grouped field when the server provides it. An
// older server returns only the flat API-host fields, so one group is derived
// from those rather than showing an empty screen.
function processHosts(snapshot) {
  if (Array.isArray(snapshot.hosts) && snapshot.hosts.length) return snapshot.hosts
  return [{
    key: 'api-host', hostname: '스카이넷 API 서버', scope: 'api-host', stale: false,
    manager: snapshot.manager, checkedAt: snapshot.checkedAt, unavailable: null,
    counts: snapshot.counts, processes: Array.isArray(snapshot.processes) ? snapshot.processes : [],
  }]
}

function dateLabel(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('ko-KR')
}
function durationLabel(milliseconds) {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '—'
  const minutes = Math.floor(milliseconds / 60000)
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  if (days) return `${days}일 ${hours}시간`
  if (hours) return `${hours}시간 ${minutes % 60}분`
  return `${minutes}분`
}
function memoryLabel(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)}GB`
  return `${Math.round(bytes / 1024 ** 2)}MB`
}

export default function PodmanOverview() {
  const [data, setData] = useState(null)
  const [dockerData, setDockerData] = useState(null)
  const [dockerError, setDockerError] = useState('')
  const [processData, setProcessData] = useState(null)
  const [error, setError] = useState('')
  const [processError, setProcessError] = useState('')
  const [loading, setLoading] = useState(false)
  const loadDocker = useCallback(async signal => {
    setDockerError('')
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token')
      const response = await fetch('/api/docker/snapshot', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', signal,
      })
      const body = await response.json()
      if (!response.ok || !body.counts || !Array.isArray(body.containers)) throw Error(body.error || 'Docker 상태를 가져오지 못했습니다.')
      if (!signal?.aborted) setDockerData(body)
    } catch (cause) {
      if (cause.name !== 'AbortError') { setDockerData(null); setDockerError(cause.message || '조회 실패') }
    }
  }, [])
  // Containers and non-container processes are read independently: one failing
  // must never render as "the other kind is not running".
  const loadProcesses = useCallback(async (signal) => {
    setProcessError('')
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token')
      const response = await fetch('/api/processes/snapshot', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', signal,
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || '프로세스 정보를 가져오지 못했습니다.')
      setProcessData(body)
    } catch (cause) {
      if (cause.name !== 'AbortError') { setProcessData(null); setProcessError(cause.message || '조회 실패') }
    }
  }, [])
  const load = useCallback(async (signal) => {
    setLoading(true); setError('')
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token')
      const response = await fetch('/api/podman/snapshot', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', signal,
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || '운영 정보를 가져오지 못했습니다.')
      setData(body)
    } catch (cause) {
      if (cause.name !== 'AbortError') { setData(null); setError(cause.message || '조회 실패') }
    } finally { if (!signal?.aborted) setLoading(false) }
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal); loadProcesses(controller.signal); loadDocker(controller.signal)
    return () => controller.abort()
  }, [load, loadProcesses, loadDocker])

  return <Page>
    <Header><div><Title>서비스 구동 현황</Title><Sub>스카이넷 API 서버에서 컨테이너로 도는 것과 프로세스로 바로 도는 것</Sub></div>
      <Refresh onClick={() => { load(); loadProcesses(); loadDocker() }} disabled={loading}><RefreshCw size={15}/>{loading ? '조회 중' : '새로고침'}</Refresh></Header>
    <Content>
      <Note>Docker·Podman은 API 서버 로컬 엔진을 각각 조회합니다. PM2는 호스트별로 표시하며, 에이전트 보고분은 미검증 상태를 구분합니다. Kubernetes Pod는 Kubernetes 화면에서 확인하세요.</Note>
      <Card aria-label="Docker 컨테이너 현황"><CardTitle><Boxes size={18}/>Docker</CardTitle>
        {dockerError ? <ErrorNotice role="alert">Docker 조회: {dockerError}</ErrorNotice> : !dockerData ? <Empty>Docker 상태 확인 중…</Empty> : <>
          <HostBlock><HostName>전체 {dockerData.counts.total} · 실행 {dockerData.counts.running} · 중지 {dockerData.counts.stopped}{dockerData.counts.other > 0 ? ` · 기타 ${dockerData.counts.other}` : ''}</HostName>
            <Sub>API 서버 로컬 Docker {dockerData.engine?.version || ''} · {dateLabel(dockerData.checkedAt)}</Sub></HostBlock>
          {dockerData.containers.length ? <TableScroll><Table><thead><tr><th>이름</th><th>이미지</th><th>상태</th><th>생성</th></tr></thead><tbody>
            {dockerData.containers.map(item => <tr key={item.id}><td><Mono>{item.name || item.id}</Mono></td><td>{item.image}</td><td>{item.status || item.state}</td><td>{dateLabel(item.created)}</td></tr>)}
          </tbody></Table></TableScroll> : <Empty>Docker 엔진에 등록된 컨테이너가 없습니다.</Empty>}
          {dockerData.truncated && <Empty>전체 수는 모든 컨테이너 기준이며, 목록은 최대 {dockerData.containerLimit}개까지 표시합니다.</Empty>}
        </>}
      </Card>
      {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
      {processError && <ErrorNotice role="alert">프로세스 조회: {processError}</ErrorNotice>}
      {processData && <Card><CardTitle><Cpu size={18}/>컨테이너 아닌 프로세스 ({processData.totals?.total ?? processData.counts?.total ?? processData.processes.length}{processData.totals ? ` · 호스트 ${processData.totals.hosts}대` : ''})</CardTitle>
        <Sub style={{ padding: '0 1.2rem' }}>
          {processData.totals ? `전체 가동 ${processData.totals.online} / 중지 ${processData.totals.stopped} / 오류 ${processData.totals.errored}` : ''}
        </Sub>
        {processHosts(processData).map(host => <HostBlock key={host.key}>
          <HostName>
            <Mono>{host.hostname}</Mono>
            {host.scope === 'api-host' ? <Tag>직접 조회</Tag> : <Tag $warn>에이전트 자가보고 · 미검증</Tag>}
            {host.stale ? <Tag $warn>보고 지연</Tag> : null}
          </HostName>
          <Sub style={{ margin: '.15rem 0 .6rem' }}>
            관리자: {host.manager?.name || '확인 불가'} {host.manager?.version || ''} · 확인 시각: {dateLabel(host.checkedAt)}
            {host.counts ? ` · 가동 ${host.counts.online} / 중지 ${host.counts.stopped} / 오류 ${host.counts.errored}` : ''}
          </Sub>
          {host.unavailable
            ? <Empty style={{ padding: '0 0 .4rem' }}>{host.unavailable === 'PROCESS_MANAGER_NOT_FOUND'
              ? '이 호스트에서 프로세스 관리자를 찾지 못했습니다. 프로세스가 없다는 뜻은 아닙니다.'
              : '이 호스트의 프로세스 상태를 가져오지 못했습니다. 프로세스가 없다는 뜻은 아닙니다.'}</Empty>
            : host.processes.length
              ? <TableScroll><Table><thead><tr><th>이름</th><th>상태</th><th>가동 시간</th><th>재시작</th><th>CPU</th><th>메모리</th><th>실행 방식</th></tr></thead><tbody>
                {host.processes.map((item, index) => <tr key={`${item.name}-${index}`}>
                  <td><Mono>{item.name || '—'}</Mono>{item.managerModule ? ' (관리 모듈)' : ''}</td>
                  <td>{item.status || '—'}</td><td>{durationLabel(item.uptimeMs)}</td>
                  <td>{item.restarts ?? '—'}{item.unstableRestarts ? ` (불안정 ${item.unstableRestarts})` : ''}</td>
                  <td>{item.cpuPercent === null || item.cpuPercent === undefined ? '—' : `${item.cpuPercent}%`}</td>
                  <td>{memoryLabel(item.memoryBytes)}</td>
                  <td>{item.execMode || '—'}{item.instances > 1 ? ` ×${item.instances}` : ''}</td>
                </tr>)}
              </tbody></Table></TableScroll>
              : <Empty style={{ padding: '0 0 .4rem' }}>프로세스 관리자에 등록된 서비스가 없습니다.</Empty>}
        </HostBlock>)}
      </Card>}
      {data && <>
        <Sub>확인 시각: {dateLabel(data.checkedAt)} · 조회 범위: API 서버 로컬 Podman</Sub>
        <Card><CardTitle>Podman 실행 구성</CardTitle>
          <TableScroll><Table><tbody>
            <tr><th>엔진 버전</th><td>{data.engine?.version || '확인 불가'}</td><th>저장소 드라이버</th><td>{data.engine?.storageDriver || '확인 불가'}</td></tr>
            <tr><th>루트리스 실행</th><td>{data.engine?.rootless === null || data.engine?.rootless === undefined ? '확인 불가' : data.engine.rootless ? '예' : '아니요'}</td><th>이벤트 기록 방식</th><td>{data.eventLogger || '확인 불가'}</td></tr>
          </tbody></Table></TableScroll>
        </Card>
        <Card><CardTitle><Boxes size={18}/>Podman 컨테이너 ({data.containers.length}{data.containers.length >= data.containerLimit ? '+' : ''})</CardTitle>
          {data.containers.length ? <TableScroll><Table><thead><tr><th>이름</th><th>이미지</th><th>상태</th><th>생성</th><th>포드</th></tr></thead><tbody>
            {data.containers.map((item, index) => <tr key={item.id || index}><td><Mono>{item.name || item.id || '—'}</Mono></td><td>{item.image || '—'}</td><td>{item.status || item.state || '—'}</td><td>{dateLabel(item.created)}</td><td>{item.pod || '—'}</td></tr>)}
          </tbody></Table></TableScroll> : <Empty>현재 조회된 컨테이너가 없습니다.</Empty>}
        </Card>
        <Card><CardTitle><Clock3 size={18}/>최근 7일 관리 이벤트 ({data.events.length}{data.events.length >= data.eventLimit ? '+' : ''})</CardTitle>
          {data.historyState === 'disabled' ? <Empty>Podman 이벤트 기록 기능이 꺼져 있어 이력을 확인할 수 없습니다.</Empty>
            : data.historyState === 'unavailable' ? <Empty>Podman 이벤트 조회에 실패했습니다. 이력이 없는 것으로 간주하지 않습니다.</Empty>
            : data.events.length ? <TableScroll><Table><thead><tr><th>시간</th><th>종류</th><th>동작</th><th>대상</th><th>이미지</th></tr></thead><tbody>
              {data.events.map((item, index) => <tr key={`${item.time}-${item.name}-${index}`}><td>{dateLabel(item.time)}</td><td>{item.type || '—'}</td><td>{item.action || '—'}</td><td><Mono>{item.name || '—'}</Mono></td><td>{item.image || '—'}</td></tr>)}
            </tbody></Table></TableScroll> : <Empty>조회 가능한 최근 이벤트가 없습니다. 이는 과거 작업이 없었다는 증거가 아닙니다.</Empty>}
          <Sub style={{ padding: '0 1.2rem 1rem' }}>출처: Podman 이벤트 기록 ({data.eventLogger || '설정 확인 불가'}). 최근 7일 중 최대 {data.eventLimit}건.</Sub>
        </Card>
      </>}
    </Content>
  </Page>
}
